import DOMPurify from "dompurify";
import type { jSendResponse, CpHubAlert } from "./cphub-alerts-types";
import "./window.portal";

const ALERT_BODY_ALLOWED_TAGS = ["a", "b", "br", "em", "i", "li", "ol", "p", "strong", "u", "ul"];
const ALERT_BODY_ALLOWED_ATTR = ["href", "rel", "target", "title"];

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

function sanitizeAlertBody(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ALERT_BODY_ALLOWED_TAGS,
    ALLOWED_ATTR: ALERT_BODY_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
}

export class CpHubAlerts {

  apiHost = "https://access.redhat.com";
  apiPath = "/api/v1/hub/alerts";
  inlineContainer: HTMLElement|null = null;
  toastContainer: HTMLElement|null = null;
  activeAlerts: Array<CpHubAlert> = [];
  toastUnmountDelay = 15000;
  toastAwaitAnimationComplete = 300;
  dismissedAlerts: Array<string|number> = [];

  /**
   * Main entry point for CpHubAlerts class.
   *
   * This method:
   *   - Retrieves and sets designated alert containers.
   *   - Fetches alerts from the API.
   *   - Dynamically builds each returned alert, populating appropriate
   *     containers.
   *   - Listens for dismiss/close events on toast alerts and populates a
   *     cookie for tracking which alerts have already been acknowledged.
   *
   * Expected API Response:
   * response = {
   *   status: string [required] One of "success", "fail", or "error."
   *   message: string [optional] Only returned when status == "error."
   *   code: string [optional] Only returned when status == "error."
   *   data: object [optional] Always returned except with status == "error."
   *     alerts: array [required] Array of actively scheduled alert objects.
   *       Empty if request is successful, but no active alerts.
   * }
   *
   * Expected Alert object from API:
   * alert = {
   *   markup: string [required] Fallback render string provided by API. For
   *     use when another key-value pair causes the dynamic build of RhAlert
   *     to fail; direct value is sent to corresponding alert container.
   *   uuid: string [required] Unique identifier for indexing.
   *   severity: string [required] Specifies the intended visual state.
   *   header: string [required] The title of the alert.
   *   body: string [required] The content of the alert, JSONEncoded.
   * }
   */
  async init() {
    // Assign alert containers.
    this.inlineContainer = document.getElementById('cphub-alerts-container');
    this.toastContainer = document.getElementById('cphub-alerts-container--toast');

    // Load the icon component.
    await window.portal.primer.onReady();
    window.portal.primer.components.include('pfe-icon');

    // Wait for the rh-alert component to exist so that we can use it.
    await window.customElements.whenDefined('rh-alert');

    // Halt execution if no alert containers exist.
    if (!this.inlineContainer && !this.toastContainer) {
      console.warn("No alert containers found on the page.");
      return;
    }

    // Update the host.
    if (window.portal.env === 'qa') {
      this.apiHost = 'https://access.qa.redhat.com';
    }
    else if (window.portal.env === 'stage') {
      this.apiHost = 'https://access.stage.redhat.com';
    }

    // Build the locale.
    let locale = window.portal.primer.locale.getCurrentLocale();
    // Handle null or en_us case.
    if (!locale || locale.toLowerCase() === "en_us") {
      locale = "en";
    }
    if (["zh_hans", "zhcn", "zh-cn", "zh_CN"].includes(locale.toLowerCase())) {
      locale = "zh-hans";
    }
    if (['en', 'de', 'fr', 'it', 'ko', 'pt', 'ru', 'ja', 'zh-hans'].indexOf(locale) < 0) {
      locale = "en";
    }

    // Parse dismissed alerts.
    this.dismissedAlerts = JSON.parse(this.getCookie('dismissed_alerts') || '[]');

    // Move toast container to `body.firstChild` to avoid z-indexing issues.
    if (this.toastContainer) {
      document.body.insertBefore(this.toastContainer, document.body.firstChild);
      /**
       *  Handle dismiss/close events on toast alerts.
       *
       *  @todo Following comment is no longer accurate outside of Drupal context.
       *  Note: This eventListener does not need to be explicitly removed, which
       *    is typically completed in the behavior's `detach` method, because
       *    the parent container it is attached to is removed/reset on
       *    subsequent executions.
       */
      this.toastContainer.addEventListener('close', (e) => this.handleDismiss(e));
    }

    // Handle dismiss/close events on inline alerts.
    if (this.inlineContainer) {
      this.inlineContainer.addEventListener('close', (e) => this.handleDismiss(e));
    }

    // Fetch active alerts and render them.
    try {
      const alerts = await this.fetchData(`${this.apiHost}${this.apiPath}?language=${locale}`);
      if (alerts.length) {
        this.activeAlerts = alerts;
        this.renderAlerts();
      } else {
        console.info("No active alerts to display.");
      }
    } catch (error) {
      console.error("Error fetching active alerts:", error);
    }
  }

  /**
   * Fetch alerts from API.
   *
   * @param {String} path
   *   The location of the Customer Portal Alerts endpoint.
   * @returns {Promise<Array>} A promise that resolves to an array of alerts.
   */
  async fetchData(path: string): Promise<CpHubAlert[]> {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`HTTP Error! Status: ${response.status}`);
    }
    const result: jSendResponse = await response.json();
    switch (result.status) {
      case 'success':
        // Return alerts if successful, and filter out dismissed alerts.
        if (Array.isArray(result?.data?.alerts)) {
          return result.data.alerts.filter((alert) => !this.isDismissed(alert.uuid));
        } else {
          console.warn('API returned, but there are no active alerts.');
        }
        break;
      case 'fail':
        console.warn('API responded with "fail." This might indicate a temporary issue.');
        console.warn('Full API Response:', result);
        return [];
      case 'error':
        console.error(`API Error [${result.code}]: ${result.message}`);
        console.error('Full API Response:', result);
        return [];
      default:
        console.warn(`Unknown API status: '${result.status}'. Treating as failure.`);
        console.warn('Full API Response:', result);
        return [];
    }
    return [];
  }

  /**
   * Renders active alerts and populates respective containers.
   */
  renderAlerts() {
    const _self = this;
    this.activeAlerts.forEach((alert) => {
      // Check if the alert is already present before adding
      if (document.querySelector(`#alert-${alert.uuid}`)) {
        return;
      }

      // Validate the alert's sections against the current path.
      let alertSectionMatch = !alert.sections || alert.sections?.length === 0;
      if (!alertSectionMatch && Array.isArray(alert.sections)) {
        // Get the current path, sans the trailing slash.
        const currentPath = window.location.pathname.endsWith('/')
          ? window.location.pathname.slice(0, -1)
          : window.location.pathname;

        for (let section of alert.sections) {
          // Ensure there's no trailing slash on the section.
          if (section.path.endsWith('/')) {
            section.path = section.path.slice(0, -1);
          }
          // Check for an exact match first.
          if (currentPath === section.path) {
            alertSectionMatch = !section.exclude;
            break;
          }
          // Handle wildcard paths.
          else if (section.path.includes('*')) {
            section.path = section.path.split('*')[0];
            // Ensure there's no trailing slash after splitting.
            if (section.path.endsWith('/')) {
              section.path = section.path.slice(0, -1);
            }
            // If the section is a substring of the path, it's a match.
            if (currentPath.indexOf(section.path) === 0) {
              alertSectionMatch = !section.exclude;
              break;
            }
          }
        }
      }

      // Exit this alert early if there's no match.
      if (!alertSectionMatch) {
        return;
      }

      const alertElement = _self.buildAlert(alert);
      if (alertElement) {
        if (alert.variant === 'inline') {
          _self.inlineContainer?.appendChild(alertElement);
        } else {
          _self.toastContainer?.appendChild(alertElement);
          if (alert.severity === 'success') {
            _self.setUnmountTimers(alertElement, alert);
          }
        }
      }

    });
  }

  /**
   * Creates a generic `rh-alert` element.
   *
   * @param {Object} alert - The alert object.
   * @returns {HTMLElement|false} - The constructed alert element, or false.
   */
  buildAlert(alert: CpHubAlert) {
    const AlertComponent = window.customElements.get('rh-alert');
    if (!AlertComponent) return false;

    const alertElement = new AlertComponent();

    this.setAlertAttributes(alertElement, alert);
    alertElement.appendChild(this.buildAlertHeader(alert));
    alertElement.appendChild(this.buildAlertBody(alert));

    return alertElement;
  }

  /**
   * Assigns attributes to an `rh-alert` element.
   *
   * @param {HTMLElement} el - The 'rh-alert' instance.
   * @param {Object} alert - The alert object.
   */
  setAlertAttributes(el: HTMLElement, alert: CpHubAlert) {
    el.id = `alert-${alert.uuid}`;
    el.setAttribute('state', alert.severity || 'default');
    el.setAttribute('variant', alert.variant || (alert.severity === 'danger' ? 'inline' : 'toast'));
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-describedby', `alert-body-${alert.uuid}`);
    el.setAttribute('aria-label', `Alert: ${alert.severity}`);
    el.setAttribute('aria-live', alert.severity === 'danger' ? 'assertive' : 'polite');

    if (alert.severity !== 'danger') {
      el.setAttribute('dismissable', 'true');
    }
  }

  /**
   * Sets auto-dismiss timers for all toast alerts.
   *
   * @param {Object} alert - The alert object.
   * @param {HTMLElement} el - The rendered `rh-alert` element.
   */
  setUnmountTimers(el: HTMLElement, alert: CpHubAlert) {
    const _self = this;
    const uuid = alert.uuid;

    // Ignore already-dismissed alerts.
    if (_self.isDismissed(uuid)) {
      return;
    }

    // Use window.setTimeout() for corrected type conversion.
    const unmountTimeout = window.setTimeout(() => {
      el.classList.add('is-animating--fade-out');

      setTimeout(() => {
        if (el.parentNode) {
          el.remove();
        }
      }, _self.toastAwaitAnimationComplete);
    }, _self.toastUnmountDelay);

    // Update the data attribute.
    el.dataset.unmountTimeout = `${unmountTimeout}`;
  }

  /**
   * Builds the header element for `rh-alert`.
   *
   * @param {CpHubAlert} alert - The alert object.
   * @returns {HTMLElement} - The built `h3[slot='header']` element.
   */
  buildAlertHeader(alert: CpHubAlert) {
    const header = document.createElement('h3');
    header.setAttribute('slot', 'header');
    // Safe to use `.textContent` because this field does not allow markup.
    header.textContent = alert.header || "Alert";

    return header;
  }

  /**
   * Builds the body element for `rh-alert`.
   *
   * @param {CpHubAlert} alert - The alert object.
   * @returns {HTMLElement} - The prepared `div` containing `alert.body`.
   */
  buildAlertBody(alert: CpHubAlert) {
    const body = document.createElement('div');
    body.id = `alert-body-${alert.uuid}`;
    body.innerHTML = sanitizeAlertBody(alert.body || 'No message provided.');

    return body;
  }

  /**
   * Handles the dismissal of an alert and stored its UUID in a cookie.
   *
   * @param {Event} event - The event triggered by the `rh-alert` component.
   */
  handleDismiss(event: Event) {
    const dismissedAlert = event.target as HTMLElement;
    if (dismissedAlert) {
      const uuid = dismissedAlert.id.replace('alert-', '');

      this.storeDismissedAlert(uuid);

      // Cancel unmount if it was scheduled.
      if (dismissedAlert.dataset.unmountTimeout) {
        clearTimeout(dismissedAlert.dataset.unmountTimeout);
      }
    }
  }

  /**
   * Stores a dismissed alert UUID in a cookie for tracking.
   *
   * @param {String} uuid - The unique identifier of the dismissed alert.
   */
  storeDismissedAlert(uuid) {
    if (!this.dismissedAlerts.includes(uuid)) {
      const element = document.querySelector(`#alert-${uuid}`);
      if (element) {
        element.remove();
      }

      this.dismissedAlerts.push(uuid);
      this.setCookie(
        'dismissed_alerts',
        encodeURIComponent(JSON.stringify(this.dismissedAlerts))
      );
    }
  }

  /**
   * Checks if an alert has been previously dismissed.
   *
   * @param {String} uuid - The unique identifier of the alert.
   * @returns {Boolean}
   *   Returns `true` if the alert has been dismissed and the UUID is present
   *   in the cookie storage array, otherwise `false`.
   */
  isDismissed(uuid: string) {
    return this.dismissedAlerts.includes(uuid);
  }

  /**
   * Retrieves a cookie value by name.
   *
   * @param {String} name - The name of the cookie to retrieve.
   * @returns {String|null} - The cookie value if found, otherwise `null`.
   */
  getCookie(name: string) {
    let match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * Sets a cookie with a specified name, value, and expiration time.
   *
   * @param {String} name - The name of the cookie.
   * @param {String} value - The value to store in the cookie.
   * @param {Number} interval
   *   A number (default: days) corresponding to cookie expiration.
   */
  setCookie(name: string, value: string) {
    document.cookie = `${name}=${value}; path=/; domain=redhat.com; SameSite=Lax`;
  }

}
